import { CompositionPlacement } from '@src/components/sidepanel/CompositionSettings/types';
import {
  VIDEO_TEMPLATE_SETTINGS_KEY,
  type VideoTemplateSettings,
} from '@src/components/sidepanel/VideoTemplateSettings/VideoTemplatesContext';
import { isHookComposition } from '@src/components/sidepanel/VideoTemplateSettings/videoTemplateUtils';
import { RemoveElementCommand } from '@src/editor';
import { AddVideoTemplateCommand } from '@src/editor/commands/AddVideoTemplateCommand';
import { err, ok } from '@src/services/common/Result';
import { createCompositionElement } from '@src/services/composition/CompositionElementService';
import { speakerFirstAppearanceService } from '@src/services/videoTemplate/SpeakerFirstAppearanceService';
import { transformKaraokeConfigToTemplate } from '@src/services/videoTemplate/VideoTemplateTransformers';
import { resolveAndApplySpeakerForCompositionElement } from '@src/utility/compositionSettings';
import {
  upsertElementMetaObject,
  type VideoLike,
} from '@src/utility/extraElementData';
import { LogManager } from '@src/utility/Logger';
import { speakerNameLine, speakerTitleLine } from '@src/utility/speakerRole';
import { v4 as uuid } from 'uuid';

import type {
  AITextControlConfiguration,
  CompositionTemplate,
  SpeakerControlConfiguration,
} from '@src/components/sidepanel/CompositionSettings/types';
import type { VideoTemplateConfigured } from '@src/components/sidepanel/VideoTemplateSettings/types';
import type { VideoEditor } from '@src/editor';
import type { Element } from '@src/editor/models/Element';
import type {
  AddMediaToTimelineOptions,
  MediaFileItem,
} from '@src/hooks/useAddMediaToTimeline';
import type { Result } from '@src/services/common/Result';
import type { Template } from '@src/services/karaoke/types';
import type { RootStore } from '@src/stores-v2/RootStore';
import type { Showcase, Story } from '@src/types.ts/story';
import type { TranscriptElementWithClipSegmentAttributes } from '@src/types.ts/transcript';
import type { ExtendedAspectRatio } from '@src/types.ts/video';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

export interface AddVideoTemplateOptions {
  videoTemplate: VideoTemplateConfigured;
  editorStore: EditorStoreLike;
  videoProps: {
    duration: number;
    /**
     * Returns the live current video. Always call this — never destructure or
     * cache the result across async boundaries. The session store replaces
     * the variant object on every `updateVariant`; any captured reference
     * becomes orphaned and writes through it are lost.
     */
    getCurrentVideo: () => VideoLike;
    aspectRatio: ExtendedAspectRatio;
    time: number;
    organization?: Showcase;
    story?: Story;
    convertToVh: (v: string) => string;
    transcriptElements?: TranscriptElementWithClipSegmentAttributes[] | null;
    storyId?: string;
  };
  upsertExtraElementDataCb: (
    elementId: string,
    template: CompositionTemplate,
  ) => void;
  addMediaToTimelineCb: (
    item: MediaFileItem,
    mediaType: 'video' | 'photo' | 'logo' | 'audio',
    options?: AddMediaToTimelineOptions,
    createUndo?: boolean,
  ) => Promise<{ success: boolean; element: Element | null }>;
  previousVideoTemplate: VideoTemplateSettings | undefined;
  applyKaraokeTemplate: (
    template: Template,
    createUndo: boolean,
  ) => Promise<void>;
  rootStore: RootStore | undefined;
  skipUndo?: boolean;
  /**
   * PLAT-11034 — forwarded to `createCompositionElement` so a template element
   * carrying an `aiText` control (today: the hook) arrives with generated text
   * instead of the template's placeholder. Optional: a caller that cannot resolve
   * what the control reads still gets its element, just unwritten.
   */
  onAITextControlsCreated?: (
    elementId: string,
    controls: ReadonlyArray<AITextControlConfiguration>,
  ) => void;
}

const logger = LogManager.getLogger('videoTemplate:VideoTemplateService');

/** PLAT-11034 — a `clip-start` hook's duration when the template authors none. */
const DEFAULT_HOOK_DURATION_SECONDS = 10;

/**
 * The authored duration, or the default. A non-positive value is a misauthored
 * template rather than a request for a zero-length graphic — it would leave a hook
 * the creator cannot grab on the timeline — so it falls back and SAYS so.
 */
function resolveHookDuration(
  authored: number | null | undefined,
  videoTemplateId: string,
): number {
  if (authored == null) return DEFAULT_HOOK_DURATION_SECONDS;
  if (authored > 0) return authored;

  logger.warn('Hook duration is not positive — using the default', {
    videoTemplateId,
    authored,
    default: DEFAULT_HOOK_DURATION_SECONDS,
  });
  return DEFAULT_HOOK_DURATION_SECONDS;
}

interface SpeakerData {
  name?: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  role?: string;
  organization?: string;
}

interface SpeakerAppearance {
  speakerLabel: string;
  firstAppearanceTime: number;
}

/** Result of creating a namecard, includes element and speaker info */
export interface NamecardResult {
  element: Element;
  speakerLabel: string;
  /** Time in seconds when the namecard appears */
  time: number;
}

/**
 * Creates intro card element if configured
 */
async function addIntroCard(
  opts: AddVideoTemplateOptions,
  createUndo: boolean,
): Promise<Element | undefined> {
  const { videoTemplate, editorStore, videoProps, upsertExtraElementDataCb } =
    opts;

  const introCard = videoTemplate.configuration?.introCardConfiguration;
  if (!introCard?.compositionTemplate) {
    return undefined;
  }

  const result = await createCompositionElement({
    template: introCard.compositionTemplate,
    editorStore,
    videoProps,
    upsertExtraElementDataCb,
    createUndoEntry: createUndo,
    locked: !!introCard.lockPosition,
  });

  if (result.ok) {
    return result.value.element;
  } else {
    logger.error('Error creating intro card', { error: result.error });
    return undefined;
  }
}

/**
 * Creates end card element if configured
 */
async function addEndCard(
  opts: AddVideoTemplateOptions,
  createUndo: boolean,
): Promise<Element | undefined> {
  const { videoTemplate, editorStore, videoProps, upsertExtraElementDataCb } =
    opts;

  const endCard = videoTemplate.configuration?.endCardConfiguration;
  if (!endCard?.compositionTemplate) {
    return undefined;
  }

  const result = await createCompositionElement({
    template: endCard.compositionTemplate,
    editorStore,
    videoProps,
    upsertExtraElementDataCb,
    createUndoEntry: createUndo,
    locked: !!endCard.lockPosition,
  });

  if (result.ok) {
    return result.value.element;
  } else {
    logger.error('Error creating end card', { error: result.error });
    return undefined;
  }
}

/**
 * PLAT-11034 — creates the template's hook if configured.
 *
 * The hook is an overlay graphic, and its PLACEMENT belongs to the composition
 * record, not to the template: `clip` spans the clip, `clip-start` is pinned to
 * where the content starts and runs for the template's authored duration. Any
 * other composition is refused — a card on `start` / `end`, or an overlay on
 * `selection` / `current_time`, would be pinned to a bound it does not
 * understand — and refused loudly, because a silently missing hook reads as a
 * broken template.
 */
async function addHook(
  opts: AddVideoTemplateOptions,
  createUndo: boolean,
): Promise<Element | undefined> {
  const {
    videoTemplate,
    editorStore,
    videoProps,
    upsertExtraElementDataCb,
    onAITextControlsCreated,
  } = opts;

  const hook = videoTemplate.configuration?.hookConfiguration;
  if (!hook?.compositionTemplate) {
    return undefined;
  }

  const template = hook.compositionTemplate;
  if (!isHookComposition(template)) {
    logger.warn(
      'Skipping the template hook — not an overlay on clip/clip-start',
      {
        videoTemplateId: videoTemplate.id,
        compositionId: template.id,
        compositionType: template.compositionType,
        placement: template.placement,
      },
    );
    return undefined;
  }

  const result = await createCompositionElement({
    template,
    editorStore,
    videoProps,
    upsertExtraElementDataCb,
    // The hook card declares an `aiText` control, so this is what makes the hook
    // arrive WRITTEN rather than showing the template's placeholder (E12).
    onAITextControlsCreated,
    createUndoEntry: createUndo,
    locked: !!hook.lockPosition,
    // Only `clip-start` has a free end edge to size; `createCompositionElement`
    // refuses the value on `clip`, where the clip's bounds ARE the duration.
    ...(template.placement === CompositionPlacement.CLIP_START && {
      durationOverride: resolveHookDuration(hook.duration, videoTemplate.id),
    }),
  });

  if (result.ok) {
    return result.value.element;
  } else {
    logger.error('Error creating the template hook', { error: result.error });
    return undefined;
  }
}

/**
 * Creates logo element if configured
 */
async function addLogo(
  opts: AddVideoTemplateOptions,
  createUndo: boolean,
): Promise<Element | undefined> {
  const { videoTemplate, addMediaToTimelineCb } = opts;

  const logoConf = videoTemplate.configuration?.logoConfiguration;
  if (!logoConf?.logo?.url) {
    return;
  }

  const logoItem: MediaFileItem = {
    id: logoConf.logo.id || uuid(),
    src: logoConf.logo.url,
    title: 'Template Logo',
    description: 'Logo from video template',
    type: 'artifact',
    aspectRatio: undefined,
  };

  const logoOptions: AddMediaToTimelineOptions = {
    ...(logoConf.opacity && { opacity: `${logoConf.opacity}%` }),
    ...(logoConf.fit && { fit: logoConf.fit }),
    locked: !!logoConf.lockPosition,
    logoPosition: {
      ...(logoConf.placementX !== null &&
        logoConf.placementX !== undefined && { x: `${logoConf.placementX}%` }),
      ...(logoConf.placementY !== null &&
        logoConf.placementY !== undefined && { y: `${logoConf.placementY}%` }),
      ...(logoConf.width !== null &&
        logoConf.width !== undefined && { width: `${logoConf.width}%` }),
      ...(logoConf.height !== null &&
        logoConf.height !== undefined && { height: `${logoConf.height}%` }),
      x_anchor: '100%',
      y_anchor: '0%',
    },
  };

  const result = await addMediaToTimelineCb(
    logoItem,
    'logo',
    logoOptions,
    createUndo,
  );
  if (result.success && result.element) {
    return result.element;
  } else {
    logger.error('Error adding logo to timeline');
    return undefined;
  }
}

/**
 * Applies captions configuration if configured
 */
async function applyCaptions(
  opts: AddVideoTemplateOptions,
  createUndo: boolean,
): Promise<Template | null> {
  const { videoTemplate, applyKaraokeTemplate } = opts;

  const captionsConfiguration =
    videoTemplate.configuration?.captionsConfiguration;
  if (!captionsConfiguration?.captionsConfig) {
    return null;
  }

  const baseTemplate = transformKaraokeConfigToTemplate(
    captionsConfiguration.captionsConfig,
  ) as Template | null;
  if (!baseTemplate) {
    return null;
  }

  // Layer per-template position/font overrides (saved by collectCaptionsPlacement)
  // on top of the underlying karaoke branding so they reach the renderer.
  const {
    verticalPosition,
    horizontalPosition,
    horizontalAlignment,
    fontSize,
  } = captionsConfiguration;
  const template: Template = {
    ...baseTemplate,
    ...(verticalPosition != null && { yPos: verticalPosition }),
    ...(horizontalPosition != null && { xPos: horizontalPosition }),
    ...(horizontalAlignment != null && { xAlignment: horizontalAlignment }),
    ...(fontSize != null && { fontSize: `${fontSize}px` }),
  };

  await applyKaraokeTemplate(template, createUndo);

  // "Lock Position": make caption lines non-interactive on the canvas. The lock
  // is written onto the karaoke *config* (not the clip instances) because the
  // clips are regenerated from KaraokeData on every recalc/transcript edit;
  // `KaraokeClip.toJSON` re-emits `locked` from the config, so regenerated clips
  // stay locked. Update each unique config via commandUpdateKaraokeConfig so the
  // change goes through the command stack (undo/redo + config map re-binding).
  applyKaraokeConfigLock(
    opts.editorStore,
    !!captionsConfiguration.lockPosition,
  );

  return template;
}

/**
 * Updates `locked` on the default karaoke config via
 * `commandUpdateKaraokeConfig`. The command handles propagating the change to
 * all clips that share the config.
 */
function applyKaraokeConfigLock(
  editorStore: EditorStoreLike,
  locked: boolean,
): void {
  const editor = editorStore.editor;
  const defaultConfig = editor.getDefaultKaraokeConfig();
  if (!defaultConfig) {
    logger.warn(
      'Captions lockPosition set but no default karaoke config found',
    );
    return;
  }

  editor.commandUpdateKaraokeConfig(
    { ...defaultConfig, locked },
    false, // no individual undo entries during bulk template application
  );
}

/**
 * Validates speaker data from DatoCMS
 */
async function validateSpeakerData(
  appearance: SpeakerAppearance,
  opts: AddVideoTemplateOptions,
): Promise<{ shouldCreate: boolean; data: SpeakerData | null }> {
  const { videoProps } = opts;
  const datoClientStore = opts.rootStore?.datoClientStore;
  if (!videoProps.storyId || !datoClientStore) {
    logger.warn(
      'Skipping speaker data check (missing storyId or datoClientStore)',
      {
        storyId: videoProps.storyId,
        datoClientStore: !!datoClientStore,
      },
    );
    return { shouldCreate: false, data: null };
  }

  try {
    const { transcriptSpeakerService } =
      await import('@src/services/transcript/TranscriptSpeakerService');
    const speakerResult = await transcriptSpeakerService.resolveSpeakerAtTime({
      storyId: videoProps.storyId,
      timeSeconds: appearance.firstAppearanceTime,
      getTranscriptElements: () => videoProps.transcriptElements,
      datoClientStore,
    });

    if (!speakerResult?.speaker) {
      logger.warn('Skipping lower third for speaker not found in DatoCMS', {
        speakerLabel: appearance.speakerLabel,
      });
      return { shouldCreate: false, data: null };
    }

    const speaker = speakerResult.speaker;
    // The lines, not the stored fields: an empty Name or Title is derived
    // from the facts (PLAT-12176).
    const hasName = speakerNameLine(speaker).length > 0;
    const hasTitle = speakerTitleLine(speaker).length > 0;

    if (!hasName && !hasTitle) {
      logger.info(
        'Skipping lower third for anonymous speaker (no name or title)',
        {
          speakerLabel: appearance.speakerLabel,
        },
      );
      return { shouldCreate: false, data: null };
    }

    logger.info('Speaker has data, will create lower third', {
      speakerLabel: appearance.speakerLabel,
      name: speaker.name,
      title: speaker.title,
    });

    return {
      shouldCreate: true,
      data: {
        name: speaker.name,
        // Seed First/Last-bound controls (PLAT-12085).
        firstName: speaker.firstName,
        lastName: speaker.lastName,
        title: speaker.title,
        role: speaker.role,
        // Seeds a Company-bound control on three-line nametags (PLAT-11998).
        organization: speaker.organization,
      },
    };
  } catch (error) {
    logger.error('Error checking speaker data', {
      speakerLabel: appearance.speakerLabel,
      error,
    });
    return { shouldCreate: false, data: null };
  }
}

/**
 * Assigns speaker data to a lower third element
 */
async function assignSpeakerToElement(
  element: Element,
  appearance: SpeakerAppearance,
  opts: AddVideoTemplateOptions,
): Promise<boolean> {
  const { editorStore, videoProps, rootStore } = opts;

  const datoClientStore = rootStore?.datoClientStore;
  if (!datoClientStore) {
    return false;
  }

  try {
    const speakerAssigned = await resolveAndApplySpeakerForCompositionElement({
      elementId: element.id,
      editorStore,
      datoClientStore,
      videoCreator: {
        storyId: videoProps.storyId,
        time: appearance.firstAppearanceTime,
        rootStore: rootStore || { transcriptionStore: undefined },
        // The showcase, for its Speaker Format (PLAT-12176).
        organization: videoProps.organization,
        // Getter — fires `videoProps.getCurrentVideo()` at each access so
        // the speakerLabel write inside `resolveAndApply...` lands on the
        // live variant. Don't change to a plain assignment; the captured
        // ref would be orphaned by any `updateVariant` running during the
        // async resolve step.
        get currentVideo() {
          return videoProps.getCurrentVideo() as VideoLike & {
            extraElementData?: Record<string, any>;
          };
        },
      },
      timelineTime: appearance.firstAppearanceTime,
      transcriptElements: videoProps.transcriptElements,
    });

    if (speakerAssigned) {
      logger.debug('Successfully assigned speaker to lower third', {
        speakerLabel: appearance.speakerLabel,
        elementId: element.id,
      });
      return true;
    } else {
      logger.warn(
        'Could not assign speaker to lower third (no speaker control)',
        {
          speakerLabel: appearance.speakerLabel,
          elementId: element.id,
        },
      );
      return false;
    }
  } catch (error) {
    logger.error('Error assigning speaker to lower third', {
      speakerLabel: appearance.speakerLabel,
      elementId: element.id,
    });
    return false;
  }
}

/**
 * Creates a single namecard element for a speaker appearance
 */
async function createNamecardForSpeaker(
  appearance: SpeakerAppearance,
  namecardTemplate: CompositionTemplate,
  opts: AddVideoTemplateOptions,
  createUndo: boolean,
): Promise<Element | undefined> {
  const { editorStore, videoProps, upsertExtraElementDataCb } = opts;

  const validation = await validateSpeakerData(appearance, opts);
  if (!validation.shouldCreate) {
    return;
  }

  // Seed the resolved speaker Name/Title at creation so the renderer shows
  // "Loading…" (or blank) instead of the template's placeholder text until the
  // async assignSpeakerToElement step lands the real values — prevents the
  // template-default flash. The SpeakerControl's bindings map the resolved
  // name/title to the correct text controls by control id.
  const speakerControl = namecardTemplate.controls.find(
    (c): c is SpeakerControlConfiguration => c.type === 'speaker',
  );

  const result = await createCompositionElement({
    template: namecardTemplate,
    editorStore,
    videoProps: {
      ...videoProps,
      time: appearance.firstAppearanceTime,
    },
    namecardValues: validation.data ?? undefined,
    // A speaker control switched off fills nothing (PLAT-11993).
    speakerControlBindings:
      speakerControl?.enabled === false
        ? undefined
        : speakerControl?.controlBindings,
    upsertExtraElementDataCb,
    createUndoEntry: createUndo,
    locked:
      !!opts.videoTemplate.configuration?.namecardConfiguration?.lockPosition,
  });

  if (!result.ok) {
    logger.error(
      'createNamecardForSpeaker: Error creating namecard for speaker',
      {
        speakerLabel: appearance.speakerLabel,
        error: result.error,
      },
    );
    return;
  }

  const element = result.value.element;

  logger.debug('createNamecardForSpeaker: Created lower third for speaker', {
    speakerLabel: appearance.speakerLabel,
    speakerName: validation.data?.name,
    time: appearance.firstAppearanceTime,
    elementId: element.id,
  });

  const assigned = await assignSpeakerToElement(element, appearance, opts);
  if (!assigned) {
    const command = new RemoveElementCommand(
      opts.editorStore.editor as VideoEditor,
      element.id,
      false,
    );
    command.execute();
    return;
  }
  return element;
}

/**
 * Creates namecard elements for all valid speaker appearances
 */
async function addNamecards(
  opts: AddVideoTemplateOptions,
  createUndo: boolean,
): Promise<NamecardResult[]> {
  const { videoTemplate, videoProps } = opts;

  const namecardConfiguration =
    videoTemplate.configuration?.namecardConfiguration;
  if (!namecardConfiguration?.compositionTemplate) {
    return [];
  }

  const speakerAppearances =
    speakerFirstAppearanceService.detectFirstAppearances(
      videoProps.transcriptElements,
      (namecardConfiguration.compositionTemplate?.source?.duration as number) ||
        5,
    );

  if (speakerAppearances.length === 0) {
    return [];
  }

  logger.info(
    `Found ${speakerAppearances.length} speaker appearances, will create lower thirds for identified speakers only`,
    {
      speakers: speakerAppearances.map(a => ({
        label: a.speakerLabel,
        time: a.firstAppearanceTime,
      })),
    },
  );
  const results: NamecardResult[] = [];
  for (let i = 0; i < speakerAppearances.length; i++) {
    const appearance = speakerAppearances[i];
    const element = await createNamecardForSpeaker(
      appearance,
      namecardConfiguration.compositionTemplate,
      opts,
      createUndo && i === 0,
    );
    if (element) {
      results.push({
        element,
        speakerLabel: appearance.speakerLabel,
        time: appearance.firstAppearanceTime,
      });
    }
  }
  return results;
}

export type VideoTemplateElementType =
  | 'logo'
  | 'introCard'
  | 'endCard'
  | 'captions'
  | 'nameCard'
  | 'hook';

/** Element entry with optional speaker label and time for namecards */
export type VideoTemplateElementResult = {
  id: string;
  type: VideoTemplateElementType;
  speakerLabel?: string;
  /** For nameCard elements, the time (in seconds) when the card appears */
  time?: number;
};

export async function addVideoTemplate(opts: AddVideoTemplateOptions): Promise<
  Result<{
    videoTemplateElements: VideoTemplateElementResult[];
  }>
> {
  const { videoTemplate, editorStore, videoProps, previousVideoTemplate } =
    opts;

  const createUndo = false; // will create a command for the entire operation
  try {
    if (!videoTemplate || !videoTemplate.id) {
      return err('VALIDATION', 'Missing video template or video template id');
    }
    if (!videoProps.getCurrentVideo()) {
      return err('PRECONDITION', 'No active video for video template');
    }

    // Execute the command FIRST to capture a snapshot of the clean state.
    // This ensures undo will restore to the state before any elements were added.
    // Note: elementIds is empty here since elements haven't been created yet,
    // but the snapshot captures the pre-change state which is what matters for undo.
    const command = new AddVideoTemplateCommand(editorStore.editor, {
      newTemplate: {
        id: videoTemplate.id,
        elementIds: [], // Will be populated after elements are added
      },
      previousTemplate: previousVideoTemplate
        ? {
            id: previousVideoTemplate.activeVideoTemplateId,
            elementIds: previousVideoTemplate.videoTemplateElements.map(e =>
              e.id.toString(),
            ),
          }
        : undefined,
      createUndoEntry: !opts.skipUndo,
    });
    editorStore.editor.executeCommand(command);

    const elements: VideoTemplateElementResult[] = [];

    // Add all configured elements and track by type
    if (
      videoTemplate.configuration?.namecardConfiguration?.enabled &&
      videoTemplate.configuration?.namecardConfiguration.compositionTemplate
    ) {
      const namecards = await addNamecards(opts, createUndo);
      elements.push(
        ...namecards.map(nc => ({
          id: nc.element.id,
          type: 'nameCard' as VideoTemplateElementType,
          speakerLabel: nc.speakerLabel,
          time: nc.time,
        })),
      );
    }
    if (
      videoTemplate.configuration?.logoConfiguration?.enabled &&
      videoTemplate.configuration?.logoConfiguration?.logo?.url
    ) {
      const logo = await addLogo(opts, createUndo);
      if (logo) {
        elements.push({ id: logo.id, type: 'logo' });
      }
    }

    if (
      videoTemplate.configuration?.endCardConfiguration?.enabled &&
      videoTemplate.configuration?.endCardConfiguration?.compositionTemplate
    ) {
      const endCard = await addEndCard(opts, createUndo);
      if (endCard) {
        elements.push({ id: endCard.id, type: 'endCard' });
      }
    }

    // Before the intro card, with the end card: both follow the clip's content
    // bounds, and the reconcile that runs after the template lands re-derives the
    // pinned edges against the settled timeline.
    if (
      videoTemplate.configuration?.hookConfiguration?.enabled &&
      videoTemplate.configuration?.hookConfiguration?.compositionTemplate
    ) {
      const hook = await addHook(opts, createUndo);
      if (hook) {
        elements.push({ id: hook.id, type: 'hook' });
      }
    }

    if (
      videoTemplate.configuration?.introCardConfiguration?.enabled &&
      videoTemplate.configuration?.introCardConfiguration?.compositionTemplate
    ) {
      const introCard = await addIntroCard(opts, createUndo); // intro card in the end because captions and end cards are relying on video duration and we want them to be placed correctly first and only then add intro card that modifies the video duration
      if (introCard) {
        elements.push({ id: introCard.id, type: 'introCard' });

        elements.forEach(e => {
          if (e.time !== undefined) {
            e.time = e.time + parseFloat(introCard.duration?.toString() || '5');
          }
        });
      }
    }
    if (
      videoTemplate.configuration?.captionsConfiguration?.enabled &&
      videoTemplate.configuration?.captionsConfiguration?.captionsConfig
    ) {
      const captions = await applyCaptions(opts, createUndo);
      if (captions) {
        elements.push({ id: captions.id as string, type: 'captions' });
      }
    }

    upsertElementMetaObject(
      videoProps.getCurrentVideo(),
      VIDEO_TEMPLATE_SETTINGS_KEY,
      {
        activeVideoTemplateId: videoTemplate.id,
        videoTemplateElements: elements,
      },
    );

    return ok({ videoTemplateElements: elements });
  } catch (error) {
    logger.warn('Unhandled exception adding video template', {
      error,
    });
    return err('UNKNOWN', 'Unhandled error adding video template', error);
  }
}

export interface AddSingleElementTypeOptions {
  videoTemplate: VideoTemplateConfigured;
  elementType: VideoTemplateElementType;
  editorStore: EditorStoreLike;
  videoProps: AddVideoTemplateOptions['videoProps'];
  upsertExtraElementDataCb: AddVideoTemplateOptions['upsertExtraElementDataCb'];
  addMediaToTimelineCb: AddVideoTemplateOptions['addMediaToTimelineCb'];
  applyKaraokeTemplate: AddVideoTemplateOptions['applyKaraokeTemplate'];
  rootStore: AddVideoTemplateOptions['rootStore'];
  onAITextControlsCreated?: AddVideoTemplateOptions['onAITextControlsCreated'];
}

export async function addSingleElementType(
  options: AddSingleElementTypeOptions,
): Promise<Result<VideoTemplateElementResult[]>> {
  const { videoTemplate, elementType } = options;

  try {
    if (!videoTemplate.configuration) {
      return err('VALIDATION', 'Video template has no configuration');
    }
    const createUndo = true; // will create a command for the single element operation since we are adding a single element

    // Build opts for the add functions
    const opts: AddVideoTemplateOptions = {
      videoTemplate,
      editorStore: options.editorStore,
      videoProps: options.videoProps,
      upsertExtraElementDataCb: options.upsertExtraElementDataCb,
      addMediaToTimelineCb: options.addMediaToTimelineCb,
      applyKaraokeTemplate: options.applyKaraokeTemplate,
      rootStore: options.rootStore,
      onAITextControlsCreated: options.onAITextControlsCreated,
      previousVideoTemplate: undefined,
    };

    const elements: VideoTemplateElementResult[] = [];

    switch (elementType) {
      case 'nameCard': {
        const result = await addNamecards(opts, createUndo);
        if (result.length > 0) {
          elements.push(
            ...result.map(nc => ({
              id: nc.element.id,
              type: 'nameCard' as VideoTemplateElementType,
              speakerLabel: nc.speakerLabel,
              time: nc.time,
            })),
          );
        }
        break;
      }
      case 'logo': {
        const logo = await addLogo(opts, createUndo);
        if (logo) {
          elements.push({ id: logo.id, type: 'logo' });
        }
        break;
      }
      case 'endCard': {
        const endCard = await addEndCard(opts, createUndo);
        if (endCard) {
          elements.push({ id: endCard.id, type: 'endCard' });
        }
        break;
      }
      case 'introCard': {
        const introCard = await addIntroCard(opts, createUndo);
        if (introCard) {
          elements.push({ id: introCard.id, type: 'introCard' });
        }
        break;
      }
      case 'hook': {
        const hook = await addHook(opts, createUndo);
        if (hook) {
          elements.push({ id: hook.id, type: 'hook' });
        }
        break;
      }
      case 'captions': {
        const captions = await applyCaptions(opts, createUndo);
        if (captions) {
          elements.push({ id: String(captions.id), type: 'captions' });
        }
        break;
      }
      default:
        return err('VALIDATION', `Unknown element type: ${elementType}`);
    }

    return ok(elements);
  } catch (error) {
    logger.error('Error adding single element type', {
      elementType,
      error,
    });
    return err('UNKNOWN', 'Error adding single element type', error);
  }
}

/** Epsilon for time comparison in seconds */
const NAMECARD_TIME_EPSILON = 0.2;

export interface ExistingNamecard {
  speakerLabel: string;
  time?: number;
}

export interface AddNamecardsForNewSpeakersOptions {
  videoTemplate: VideoTemplateConfigured;
  editorStore: EditorStoreLike;
  videoProps: AddVideoTemplateOptions['videoProps'];
  upsertExtraElementDataCb: AddVideoTemplateOptions['upsertExtraElementDataCb'];
  rootStore: AddVideoTemplateOptions['rootStore'];
  /** Existing namecards with speaker labels and times - will be excluded */
  existingNamecards: ExistingNamecard[];
}

/**
 * Checks if a namecard already exists for the given speaker and time.
 * Uses epsilon comparison for time (0.2s tolerance).
 */
function hasExistingNamecard(
  existingNamecards: ExistingNamecard[],
  speakerLabel: string,
  time: number,
): boolean {
  return existingNamecards.some(nc => {
    if (nc.speakerLabel !== speakerLabel) return false;
    // If existing namecard has no time (legacy), match by speaker only
    if (nc.time === undefined) return true;
    // Compare times with epsilon
    return Math.abs(nc.time - time) <= NAMECARD_TIME_EPSILON;
  });
}

/**
 * Creates namecards for speakers that don't already have one.
 * Used when transcript content is restored and new speakers appear.
 */
export async function addNamecardsForNewSpeakers(
  options: AddNamecardsForNewSpeakersOptions,
): Promise<Result<VideoTemplateElementResult[]>> {
  const {
    videoTemplate,
    editorStore,
    videoProps,
    upsertExtraElementDataCb,
    rootStore,
    existingNamecards,
  } = options;

  try {
    const namecardConfiguration =
      videoTemplate.configuration?.namecardConfiguration;
    if (!namecardConfiguration?.compositionTemplate) {
      logger.debug('No namecard configuration, skipping auto-add');
      return ok([]);
    }

    if (!namecardConfiguration.enabled) {
      logger.debug('Namecards disabled in template, skipping auto-add');
      return ok([]);
    }

    // Detect all speaker first appearances
    const allAppearances = speakerFirstAppearanceService.detectFirstAppearances(
      videoProps.transcriptElements,
      (namecardConfiguration.compositionTemplate?.source?.duration as number) ||
        5,
    );

    // Filter to only new speaker appearances (not already covered by existing namecards)
    const newSpeakerAppearances = allAppearances.filter(
      appearance =>
        !hasExistingNamecard(
          existingNamecards,
          appearance.speakerLabel,
          appearance.firstAppearanceTime,
        ),
    );

    if (newSpeakerAppearances.length === 0) {
      logger.debug('No new speaker appearances found, nothing to add');
      return ok([]);
    }

    logger.info(
      `Found ${newSpeakerAppearances.length} new speaker appearances, creating namecards`,
      {
        newSpeakers: newSpeakerAppearances.map(a => ({
          label: a.speakerLabel,
          time: a.firstAppearanceTime,
        })),
        existingNamecards: existingNamecards.map(nc => ({
          label: nc.speakerLabel,
          time: nc.time,
        })),
      },
    );

    // Build opts for createNamecardForSpeaker
    const opts: AddVideoTemplateOptions = {
      videoTemplate,
      editorStore,
      videoProps,
      upsertExtraElementDataCb,
      addMediaToTimelineCb: async () => ({ success: false, element: null }), // Not used for namecards
      applyKaraokeTemplate: async () => {}, // Not used for namecards
      rootStore,
      previousVideoTemplate: undefined,
    };

    const results: VideoTemplateElementResult[] = [];
    for (let i = 0; i < newSpeakerAppearances.length; i++) {
      const appearance = newSpeakerAppearances[i];
      const element = await createNamecardForSpeaker(
        appearance,
        namecardConfiguration.compositionTemplate,
        opts,
        i === 0, // Create undo for first element only
      );
      if (element) {
        results.push({
          id: element.id,
          type: 'nameCard',
          speakerLabel: appearance.speakerLabel,
          time: appearance.firstAppearanceTime,
        });
      }
    }

    return ok(results);
  } catch (error) {
    logger.error('Error adding namecards for new speakers', { error });
    return err('UNKNOWN', 'Error adding namecards for new speakers', error);
  }
}

export interface ExistingNamecardElement {
  elementId: string;
  speakerLabel: string;
  time?: number;
}

export interface NamecardSyncResult {
  /** Speaker appearances that need new namecards (genuinely new speakers) */
  toAdd: { speakerLabel: string; time: number }[];
  /** Element IDs of namecards that should be removed (speaker no longer in transcript) */
  toRemove: string[];
  /** Existing namecards whose time metadata needs updating (same speaker, different time) */
  toUpdate: {
    elementId: string;
    speakerLabel: string;
    newTime: number;
  }[];
}

export interface ComputeNamecardSyncOptions {
  transcriptElements: AddVideoTemplateOptions['videoProps']['transcriptElements'];
  /** Current namecards on canvas with their speaker labels and times */
  existingNamecards: ExistingNamecardElement[];
  /** Minimum gap between speaker appearances (duration of namecard) */
  minGapSeconds: number;
}

/**
 * Computes a signature string from transcript elements representing the current
 * speaker state. Used to detect when speakers change (added/removed) to trigger
 * namecard sync.
 *
 * @param transcriptElements - Array of transcript elements
 * @returns A JSON string signature of active speakers with their first appearance times
 */
export function getSpeakerSignature(
  transcriptElements:
    | TranscriptElementWithClipSegmentAttributes[]
    | null
    | undefined,
): string {
  if (!transcriptElements || !Array.isArray(transcriptElements)) {
    return '';
  }

  const activeSpeakersFirstAppear: { speakerLabel: string; time: number }[] =
    [];
  const seenSpeakers = new Set<string>();

  for (const el of transcriptElements) {
    if (
      el.type === 'text' &&
      el.speaker_label &&
      el.state !== 'removed' &&
      el.state !== 'cut' &&
      typeof el.ts === 'number' &&
      !seenSpeakers.has(el.speaker_label)
    ) {
      seenSpeakers.add(el.speaker_label);
      activeSpeakersFirstAppear.push({
        speakerLabel: el.speaker_label,
        time: el.ts,
      });
    }
  }

  return JSON.stringify(
    activeSpeakersFirstAppear.sort((a, b) => a.time - b.time),
  );
}

/**
 * Computes what namecard changes are needed to sync with current transcript.
 * Matches existing namecards to desired appearances by speakerLabel (identity).
 * - Same speaker, same time (within epsilon) → no action
 * - Same speaker, different time → toUpdate (preserves user edits like position)
 * - New speaker → toAdd
 * - Speaker no longer in transcript → toRemove
 */
export function computeNamecardSync(
  options: ComputeNamecardSyncOptions,
): NamecardSyncResult {
  const { transcriptElements, existingNamecards, minGapSeconds } = options;

  // Step 1: Compute desired state - what namecards SHOULD exist
  const desiredAppearances =
    speakerFirstAppearanceService.detectFirstAppearances(
      transcriptElements,
      minGapSeconds,
    );

  // Step 2: Index existing namecards by speaker label
  const existingBySpeaker = new Map<string, ExistingNamecardElement>();
  for (const nc of existingNamecards) {
    // One namecard per speaker (detectFirstAppearances returns one per speaker)
    existingBySpeaker.set(nc.speakerLabel, nc);
  }

  const toAdd: { speakerLabel: string; time: number }[] = [];
  const toUpdate: {
    elementId: string;
    speakerLabel: string;
    newTime: number;
  }[] = [];
  const matchedSpeakers = new Set<string>();

  // Step 3: For each desired appearance, determine action
  for (const desired of desiredAppearances) {
    const existing = existingBySpeaker.get(desired.speakerLabel);

    if (!existing) {
      // No existing namecard for this speaker → add
      toAdd.push({
        speakerLabel: desired.speakerLabel,
        time: desired.firstAppearanceTime,
      });
      continue;
    }

    matchedSpeakers.add(desired.speakerLabel);

    // Legacy namecards without time → already matched, no time update needed
    if (existing.time === undefined) {
      continue;
    }

    // Check if time differs beyond epsilon
    const timeDiff = Math.abs(existing.time - desired.firstAppearanceTime);
    if (timeDiff > NAMECARD_TIME_EPSILON) {
      toUpdate.push({
        elementId: existing.elementId,
        speakerLabel: desired.speakerLabel,
        newTime: desired.firstAppearanceTime,
      });
    }
  }

  // Step 4: Existing namecards whose speaker is no longer desired → remove
  const toRemove: string[] = [];
  for (const existing of existingNamecards) {
    if (!matchedSpeakers.has(existing.speakerLabel)) {
      // Check if this speaker has a desired appearance (could be in toAdd
      // if existingBySpeaker didn't have it — shouldn't happen, but guard)
      const stillDesired = desiredAppearances.some(
        d => d.speakerLabel === existing.speakerLabel,
      );
      if (!stillDesired) {
        toRemove.push(existing.elementId);
        logger.debug('Namecard no longer needed, marking for removal', {
          speakerLabel: existing.speakerLabel,
          time: existing.time,
          elementId: existing.elementId,
        });
      }
    }
  }

  if (toAdd.length > 0 || toRemove.length > 0 || toUpdate.length > 0) {
    logger.info('Namecard sync computed', {
      toAdd: toAdd.map(a => ({ speaker: a.speakerLabel, time: a.time })),
      toRemove,
      toUpdate: toUpdate.map(u => ({
        elementId: u.elementId,
        speaker: u.speakerLabel,
        newTime: u.newTime,
      })),
      desired: desiredAppearances.map(d => ({
        speaker: d.speakerLabel,
        time: d.firstAppearanceTime,
      })),
      existing: existingNamecards.map(e => ({
        speaker: e.speakerLabel,
        time: e.time,
        id: e.elementId,
      })),
    });
  }

  return { toAdd, toRemove, toUpdate };
}

export interface CreateNamecardsOptions {
  videoTemplate: VideoTemplateConfigured;
  editorStore: EditorStoreLike;
  videoProps: AddVideoTemplateOptions['videoProps'];
  upsertExtraElementDataCb: AddVideoTemplateOptions['upsertExtraElementDataCb'];
  rootStore: AddVideoTemplateOptions['rootStore'];
  /** Speaker appearances to create namecards for */
  appearances: { speakerLabel: string; time: number }[];
}

/**
 * Creates namecards for the given speaker appearances.
 * Used after computeNamecardSync to create the namecards that need to be added.
 */
export async function createNamecardsFromSyncResult(
  options: CreateNamecardsOptions,
): Promise<Result<VideoTemplateElementResult[]>> {
  const {
    videoTemplate,
    editorStore,
    videoProps,
    upsertExtraElementDataCb,
    rootStore,
    appearances,
  } = options;

  try {
    const namecardConfiguration =
      videoTemplate.configuration?.namecardConfiguration;
    if (!namecardConfiguration?.compositionTemplate) {
      return ok([]);
    }

    // Build opts for createNamecardForSpeaker
    const opts: AddVideoTemplateOptions = {
      videoTemplate,
      editorStore,
      videoProps,
      upsertExtraElementDataCb,
      addMediaToTimelineCb: async () => ({ success: false, element: null }),
      applyKaraokeTemplate: async () => {},
      rootStore,
      previousVideoTemplate: undefined,
    };

    const results: VideoTemplateElementResult[] = [];
    for (let i = 0; i < appearances.length; i++) {
      const appearance = appearances[i];
      const element = await createNamecardForSpeaker(
        {
          speakerLabel: appearance.speakerLabel,
          firstAppearanceTime: appearance.time,
        },
        namecardConfiguration.compositionTemplate,
        opts,
        i === 0, // Create undo for first element only
      );
      if (element) {
        results.push({
          id: element.id,
          type: 'nameCard',
          speakerLabel: appearance.speakerLabel,
          time: appearance.time,
        });
      }
    }

    return ok(results);
  } catch (error) {
    logger.error('Error creating namecards from sync result', { error });
    return err('UNKNOWN', 'Error creating namecards', error);
  }
}
